import React, { useEffect, useState } from "react";

/**
 * Drop-in replacement for react-reveal/Fade which is incompatible with React 18.
 * Supports the same props used across the codebase:
 *   bottom, distance, duration, when, mountOnEnter, unmountOnExit, delay, left, right, top
 */

interface FadeProps {
  children: React.ReactNode;
  bottom?: boolean;
  top?: boolean;
  left?: boolean;
  right?: boolean;
  distance?: string;
  duration?: number;
  delay?: number;
  when?: boolean;
  mountOnEnter?: boolean;
  unmountOnExit?: boolean;
  cascade?: boolean;
  [key: string]: any;
}

const Fade: React.FC<FadeProps> = ({
  children,
  bottom = false,
  top = false,
  left = false,
  right = false,
  distance = "0px",
  duration = 1000,
  delay = 0,
  when = true,
  mountOnEnter = false,
  unmountOnExit = false,
  ...rest
}) => {
  const [visible, setVisible] = useState(when);
  const [mounted, setMounted] = useState(!mountOnEnter || when);

  useEffect(() => {
    if (when) {
      setMounted(true);
      // Small delay to ensure mount happens before animation
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      if (unmountOnExit) {
        const timer = setTimeout(() => setMounted(false), duration);
        return () => clearTimeout(timer);
      }
    }
  }, [when, duration, unmountOnExit]);

  if (!mounted) return null;

  let translateX = "0";
  let translateY = "0";

  if (bottom) translateY = distance || "20px";
  if (top) translateY = `-${distance || "20px"}`;
  if (left) translateX = `-${distance || "20px"}`;
  if (right) translateX = distance || "20px";

  const style: React.CSSProperties = {
    transition: `opacity ${duration}ms ease, transform ${duration}ms ease`,
    transitionDelay: `${delay}ms`,
    opacity: visible ? 1 : 0,
    transform: visible ? "translate3d(0, 0, 0)" : `translate3d(${translateX}, ${translateY}, 0)`,
  };

  return <div style={style}>{children}</div>;
};

export default Fade;
